import { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="contact-title"
          >
            Get In Touch
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          <p
            className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Have questions or want to know more about our ministry? We'd love to hear from you
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Contact Information
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-gray-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Address</h4>
                    <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: "'Crimson Text', serif" }}>
                      Waec Junction, Plot 3 Aguleri Street,<br />
                      Independence Layout, Enugu,<br />
                      Abuja, Enugu
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-gray-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Email</h4>
                    <a
                      href="mailto:contacts@bannerofgraceministry.org"
                      className="text-amber-700 hover:text-amber-800 text-sm"
                      style={{ fontFamily: "'Crimson Text', serif" }}
                    >
                      contacts@bannerofgraceministry.org
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Times */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Service Times
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-semibold text-gray-900" style={{ fontFamily: "'Crimson Text', serif" }}>Sunday Service</span>
                  <span className="text-gray-700" style={{ fontFamily: "'Crimson Text', serif" }}>9:00 AM</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-semibold text-gray-900" style={{ fontFamily: "'Crimson Text', serif" }}>Tuesday Prayer</span>
                  <span className="text-gray-700" style={{ fontFamily: "'Crimson Text', serif" }}>6:00 PM</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-semibold text-gray-900" style={{ fontFamily: "'Crimson Text', serif" }}>Thursday Bible Study</span>
                  <span className="text-gray-700" style={{ fontFamily: "'Crimson Text', serif" }}>6:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3
              className="text-2xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Send Us a Message
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm" style={{ fontFamily: "'Crimson Text', serif" }}>
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm" style={{ fontFamily: "'Crimson Text', serif" }}>
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm" style={{ fontFamily: "'Crimson Text', serif" }}>
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="Message subject"
                  data-testid="contact-subject-input"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm" style={{ fontFamily: "'Crimson Text', serif" }}>
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent resize-none"
                  placeholder="Your message..."
                  data-testid="contact-message-input"
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                <Send size={20} />
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};